import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useLibraryPostsGateway } from './LibraryPostsGatewayProvider';
import { applyEngagementChange } from '../api/libraryPostCache';
import type { EngageableLibraryPost } from '../model/types';
import { useLibraryFilters } from '../state/libraryFiltersStore';

import { type SortOption } from '@/constants/community';
import { useUserScope } from '@/hooks/useUserScope';
import { trackCommunityPostLikeToggled } from '@/util/analyticsEvents';

/**
 * The like/unlike toggle for an Inquiry Library post.
 *
 * `q` must be the search term the screen fetched the post with, since the
 * post's cache key includes it, or the optimistic update misses the entry the
 * screen is reading.
 *
 * A failed request rolls the post back and then calls `onFailed` with the state
 * the reader asked for, so the caller can say so; a hook draws nothing.
 */
export function useToggleLikedPost({
  item,
  cancerTypeId,
  sortBy,
  q,
  onFailed,
}: {
  item: EngageableLibraryPost;
  cancerTypeId?: number;
  sortBy?: SortOption;
  q?: string;
  onFailed?: (variables: { liked: boolean }) => void;
}) {
  const queryClient = useQueryClient();

  const { userId } = useUserScope();

  const gateway = useLibraryPostsGateway();

  const searchQuery = useLibraryFilters((state) => state.searchQuery) || undefined;

  const showSavedPosts = useLibraryFilters((state) => state.showSavedPosts) || undefined;

  const postId = Number(item.id);

  const liked = Boolean(item.liked);

  const { mutate: setLiked } = useMutation({
    mutationFn: (variables: { postId: number; liked: boolean }) =>
      gateway.setLiked(variables.postId, variables.liked),
    onMutate: (variables) =>
      applyEngagementChange(queryClient, {
        userId,
        postId: variables.postId,
        change: { kind: 'like' },
        view: { cancerTypeId, searchQuery, showSavedPosts, sortBy },
        postQ: q,
      }),
    onSuccess: (_data, variables) => {
      trackCommunityPostLikeToggled({ postId: variables.postId, liked: variables.liked });
    },
    onError: (error, variables, change) => {
      console.error(`useToggleLikedPost:setLiked:${JSON.stringify(variables)}:ERROR:`, error);

      change?.rollback();

      onFailed?.({ liked: variables.liked });
    },
    onSettled: (_data, _error, _variables, change) => {
      change?.settle();
    },
  });

  const toggleLiked = () => setLiked({ postId, liked: !liked });

  return { liked, toggleLiked };
}
